import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton,
    Chip,
    Switch,
    FormControlLabel,
    Autocomplete,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    NetworkCheck as NetworkCheckIcon,
    CreditCard as CreditCardIcon,
    CurrencyRupee as CurrencyRupeeIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';
import { COUNTRIES, codesToCountries } from '../constants/countries';

// ── Stripe fields ────────────────────────────────────────────────────────────

const STRIPE_FIELDS = [
    {
        key: 'stripe_secret_key',
        label: 'Secret Key',
        placeholder: 'sk_live_... or sk_test_...',
        hint: 'Server-side secret key. Never exposed to the browser.',
        secret: true,
    },
    {
        key: 'stripe_publishable_key',
        label: 'Publishable Key',
        placeholder: 'pk_live_... or pk_test_...',
        hint: 'Safe to expose in the frontend for Stripe.js.',
        secret: false,
    },
    {
        key: 'stripe_webhook_secret',
        label: 'Webhook Secret',
        placeholder: 'whsec_...',
        hint: 'Found in the Stripe Dashboard → Webhooks → your endpoint.',
        secret: true,
    },
];

// ── Razorpay fields ──────────────────────────────────────────────────────────

const RAZORPAY_FIELDS = [
    {
        key: 'razorpay_key_id',
        label: 'Key ID',
        placeholder: 'rzp_live_... or rzp_test_...',
        hint: 'Public key ID — shown on the Razorpay checkout modal.',
        secret: false,
    },
    {
        key: 'razorpay_key_secret',
        label: 'Key Secret',
        placeholder: 'Leave blank to keep existing secret',
        hint: 'Server-side secret. Never exposed to the browser.',
        secret: true,
    },
    {
        key: 'razorpay_webhook_secret',
        label: 'Webhook Secret',
        placeholder: 'Leave blank to keep existing secret',
        hint: 'Set in the Razorpay Dashboard → Webhooks → your endpoint.',
        secret: true,
    },
];

// ── SecretField ──────────────────────────────────────────────────────────────

function SecretField({ field, value, showSecrets, onToggle, onChange }) {
    const isSecret = field.secret;
    return (
        <Box>
            <TextField
                fullWidth
                label={field.label}
                placeholder={field.placeholder}
                value={value}
                onChange={(e) => onChange(field.key, e.target.value)}
                type={isSecret && !showSecrets ? 'password' : 'text'}
                size="small"
                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                InputProps={isSecret ? {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={onToggle}
                                edge="end"
                                aria-label={showSecrets ? 'Hide secrets' : 'Show secrets'}
                            >
                                {showSecrets
                                    ? <VisibilityOffIcon fontSize="small" />
                                    : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                } : undefined}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {field.hint}
            </Typography>
        </Box>
    );
}

// ── GatewayCard — netflix card design ────────────────────────────────────────

function GatewayCard({
    title, icon, accentColor, fields,
    values, onValueChange,
    enabled, onEnabledChange,
    countriesSelected, onCountriesChange,
    showSecrets, onToggleSecrets,
    saving, testing, error, success, connectionStatus,
    onSave, onTest,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            border: `1.5px solid ${alpha(accentColor, isDark ? 0.22 : 0.18)}`,
            background: isDark
                ? `linear-gradient(145deg, ${alpha(accentColor, 0.07)} 0%, ${alpha('#0f172a', 0.92)} 100%)`
                : `linear-gradient(145deg, ${alpha(accentColor, 0.05)} 0%, #ffffff 100%)`,
            backdropFilter: 'blur(16px)',
            transition: 'all 0.25s ease',
            mb: 4,
            '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 20px 48px -8px ${alpha(accentColor, 0.22)}`,
                borderColor: alpha(accentColor, 0.38),
            },
        }}>
            {/* Ambient glow blob */}
            <Box sx={{
                position: 'absolute', top: -50, right: -50,
                width: 160, height: 160, borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(accentColor, 0.12)} 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />

            <Box sx={{ p: 3, position: 'relative' }}>
                {/* Card header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(accentColor, 0.12),
                            border: `1px solid ${alpha(accentColor, 0.25)}`,
                        }}>
                            {icon}
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                                {title}
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: alpha(accentColor, 0.85),
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                fontSize: '0.65rem',
                            }}>
                                Payment Gateway
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {connectionStatus !== null && (
                            <Chip
                                icon={connectionStatus
                                    ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                                    : <CancelIcon sx={{ fontSize: '14px !important' }} />
                                }
                                label={connectionStatus ? 'Connected' : 'Failed'}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.68rem',
                                    borderRadius: 99,
                                    height: 26,
                                    bgcolor: connectionStatus
                                        ? alpha('#10b981', 0.12)
                                        : alpha('#ef4444', 0.12),
                                    color: connectionStatus ? '#10b981' : '#ef4444',
                                    border: `1px solid ${connectionStatus
                                        ? alpha('#10b981', 0.3)
                                        : alpha('#ef4444', 0.3)}`,
                                    '& .MuiChip-icon': { color: 'inherit' },
                                }}
                            />
                        )}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    onChange={(e) => onEnabledChange(e.target.checked)}
                                    size="small"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            bgcolor: alpha(accentColor, 0.5),
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="caption" sx={{
                                    fontWeight: 700,
                                    color: enabled ? accentColor : 'text.disabled',
                                }}>
                                    {enabled ? 'Enabled' : 'Disabled'}
                                </Typography>
                            }
                            sx={{ m: 0 }}
                        />
                    </Box>
                </Box>

                {/* Thin accent divider */}
                <Box sx={{
                    height: '1px',
                    background: `linear-gradient(90deg, ${alpha(accentColor, 0.4)}, transparent)`,
                    mb: 3,
                }} />

                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>
                )}

                {/* API key fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                    {fields.map((field) => (
                        <SecretField
                            key={field.key}
                            field={field}
                            value={values[field.key] || ''}
                            showSecrets={showSecrets}
                            onToggle={onToggleSecrets}
                            onChange={onValueChange}
                        />
                    ))}
                </Box>

                {/* Country multi-select */}
                <Box sx={{ mb: 0.5 }}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={COUNTRIES}
                        value={countriesSelected}
                        onChange={(_, newValue) => onCountriesChange(newValue)}
                        getOptionLabel={(o) => `${o.label} (${o.code})`}
                        isOptionEqualToValue={(o, v) => o.code === v.code}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Allowed Countries"
                                placeholder={countriesSelected.length === 0 ? 'All countries (leave empty for global)' : 'Search…'}
                                size="small"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option.code}
                                    label={`${option.code} · ${option.label}`}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.68rem',
                                        borderRadius: 99,
                                        height: 22,
                                        bgcolor: alpha(accentColor, 0.1),
                                        color: accentColor,
                                        border: `1px solid ${alpha(accentColor, 0.25)}`,
                                        '& .MuiChip-deleteIcon': { color: alpha(accentColor, 0.6) },
                                    }}
                                />
                            ))
                        }
                    />
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Leave empty to allow all countries. Select countries to restrict this gateway to specific regions.
                </Typography>

                {/* Thin accent divider */}
                <Box sx={{
                    height: '1px',
                    background: `linear-gradient(90deg, ${alpha(accentColor, 0.2)}, transparent)`,
                    my: 3,
                }} />

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
                        onClick={onSave}
                        disabled={saving || testing}
                        sx={{
                            bgcolor: accentColor,
                            '&:hover': { bgcolor: accentColor, filter: 'brightness(1.1)' },
                            '&:disabled': { bgcolor: alpha(accentColor, 0.4) },
                            borderRadius: '10px',
                            fontWeight: 700,
                            textTransform: 'none',
                        }}
                    >
                        {saving ? 'Saving…' : 'Save Changes'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <NetworkCheckIcon />}
                        onClick={onTest}
                        disabled={saving || testing}
                        sx={{
                            color: accentColor,
                            borderColor: alpha(accentColor, 0.4),
                            '&:hover': {
                                borderColor: accentColor,
                                bgcolor: alpha(accentColor, 0.05),
                            },
                            borderRadius: '10px',
                            fontWeight: 700,
                            textTransform: 'none',
                        }}
                    >
                        {testing ? 'Testing…' : 'Test Connection'}
                    </Button>
                </Box>
            </Box>

            {/* Bottom accent bar */}
            <Box sx={{
                height: 3,
                background: `linear-gradient(90deg, ${accentColor}, ${alpha(accentColor, 0.2)})`,
            }} />
        </Box>
    );
}

// ── Initial values ────────────────────────────────────────────────────────────

const INITIAL_STRIPE = {
    stripe_secret_key:      '',
    stripe_publishable_key: '',
    stripe_webhook_secret:  '',
};

const INITIAL_RAZORPAY = {
    razorpay_key_id:         '',
    razorpay_key_secret:     '',
    razorpay_webhook_secret: '',
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminGatewaySettings() {
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState('');

    // Stripe state
    const [stripeValues, setStripeValues]                   = useState(INITIAL_STRIPE);
    const [stripeEnabled, setStripeEnabled]                 = useState(true);
    const [stripeCountriesSelected, setStripeCountriesSelected] = useState([]);
    const [stripeShowSecrets, setStripeShowSecrets]         = useState(false);
    const [stripeSaving, setStripeSaving]                   = useState(false);
    const [stripeTesting, setStripeTesting]                 = useState(false);
    const [stripeError, setStripeError]                     = useState('');
    const [stripeSuccess, setStripeSuccess]                 = useState('');
    const [stripeStatus, setStripeStatus]                   = useState(null);

    // Razorpay state
    const [rzpValues, setRzpValues]                   = useState(INITIAL_RAZORPAY);
    const [rzpEnabled, setRzpEnabled]                 = useState(false);
    const [rzpCountriesSelected, setRzpCountriesSelected] = useState([{ code: 'IN', label: 'India' }]);
    const [rzpShowSecrets, setRzpShowSecrets]         = useState(false);
    const [rzpSaving, setRzpSaving]                   = useState(false);
    const [rzpTesting, setRzpTesting]                 = useState(false);
    const [rzpError, setRzpError]                     = useState('');
    const [rzpSuccess, setRzpSuccess]                 = useState('');
    const [rzpStatus, setRzpStatus]                   = useState(null);

    const fetchConfig = useCallback(async () => {
        try {
            setLoading(true);
            setPageError('');
            const data = await adminApi.getGatewayConfig();

            setStripeValues({
                stripe_secret_key:      data.stripe_secret_key      || '',
                stripe_publishable_key: data.stripe_publishable_key || '',
                stripe_webhook_secret:  data.stripe_webhook_secret  || '',
            });
            setStripeEnabled(data.stripe_enabled !== false);
            setStripeCountriesSelected(codesToCountries(data.stripe_countries || []));

            setRzpValues({
                razorpay_key_id:         data.razorpay_key_id         || '',
                razorpay_key_secret:     data.razorpay_key_secret     || '',
                razorpay_webhook_secret: data.razorpay_webhook_secret || '',
            });
            setRzpEnabled(!!data.razorpay_enabled);
            setRzpCountriesSelected(codesToCountries(data.razorpay_countries || ['IN']));
        } catch (err) {
            setPageError(err.message || 'Failed to load gateway configuration');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    // ── Stripe handlers ───────────────────────────────────────────────────────

    const handleStripeValueChange = (key, val) => {
        setStripeValues(prev => ({ ...prev, [key]: val }));
        setStripeError('');
        setStripeSuccess('');
        setStripeStatus(null);
    };

    const handleStripeCountriesChange = (newValue) => {
        setStripeCountriesSelected(newValue);
        setStripeError('');
        setStripeSuccess('');
        setStripeStatus(null);
    };

    const handleStripeSave = async () => {
        setStripeSaving(true);
        setStripeError('');
        setStripeSuccess('');
        setStripeStatus(null);
        try {
            await adminApi.updateGatewayConfig({
                ...stripeValues,
                stripe_enabled:   stripeEnabled,
                stripe_countries: stripeCountriesSelected.map(c => c.code),
            });
            setStripeSuccess('Stripe configuration saved.');
            await fetchConfig();
        } catch (err) {
            setStripeError(err.message || 'Failed to save Stripe configuration');
        } finally {
            setStripeSaving(false);
        }
    };

    const handleStripeTest = async () => {
        setStripeTesting(true);
        setStripeError('');
        setStripeSuccess('');
        setStripeStatus(null);
        try {
            const result = await adminApi.testStripeConnection();
            setStripeStatus(result.connected);
            if (result.connected) {
                setStripeSuccess('Stripe connection test passed.');
            } else {
                setStripeError(result.message || 'Stripe connection failed. Check your secret key.');
            }
        } catch (err) {
            setStripeStatus(false);
            setStripeError(err.message || 'Connection test failed');
        } finally {
            setStripeTesting(false);
        }
    };

    // ── Razorpay handlers ─────────────────────────────────────────────────────

    const handleRzpValueChange = (key, val) => {
        setRzpValues(prev => ({ ...prev, [key]: val }));
        setRzpError('');
        setRzpSuccess('');
        setRzpStatus(null);
    };

    const handleRzpCountriesChange = (newValue) => {
        setRzpCountriesSelected(newValue);
        setRzpError('');
        setRzpSuccess('');
        setRzpStatus(null);
    };

    const handleRzpSave = async () => {
        setRzpSaving(true);
        setRzpError('');
        setRzpSuccess('');
        setRzpStatus(null);
        try {
            await adminApi.updateGatewayConfig({
                ...rzpValues,
                razorpay_enabled:   rzpEnabled,
                razorpay_countries: rzpCountriesSelected.map(c => c.code),
            });
            setRzpSuccess('Razorpay configuration saved.');
            await fetchConfig();
        } catch (err) {
            setRzpError(err.message || 'Failed to save Razorpay configuration');
        } finally {
            setRzpSaving(false);
        }
    };

    const handleRzpTest = async () => {
        setRzpTesting(true);
        setRzpError('');
        setRzpSuccess('');
        setRzpStatus(null);
        try {
            const result = await adminApi.testRazorpayConnection();
            setRzpStatus(result.connected);
            if (result.connected) {
                setRzpSuccess('Razorpay connection test passed.');
            } else {
                setRzpError(result.message || 'Razorpay connection failed. Check your credentials.');
            }
        } catch (err) {
            setRzpStatus(false);
            setRzpError(err.message || 'Connection test failed');
        } finally {
            setRzpTesting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (pageError) {
        return (
            <Box sx={{ maxWidth: 760, mx: 'auto', p: 2 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={fetchConfig}>
                            Retry
                        </Button>
                    }
                >
                    {pageError}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 760, mx: 'auto', p: 2 }}>
            {/* Page header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
                    Payment Gateway Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Manage Stripe and Razorpay credentials, enable/disable each gateway, and control country availability.
                    All values are stored in Firestore and take effect immediately — no redeploy needed.
                </Typography>
            </Box>

            {/* Info card — netflix style, neutral */}
            <Box sx={{
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                background: (theme) => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.6)
                    : alpha('#f8fafc', 0.9),
                p: 2.5,
                mb: 4,
            }}>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    How country filtering works
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75, fontSize: '0.85rem', color: 'text.secondary' } }}>
                    <li>The pricing page checks the user's country and displays the matching gateway.</li>
                    <li>If <strong>Razorpay countries</strong> includes <code>IN</code> and the user is in India, Razorpay is shown.</li>
                    <li>If <strong>Stripe countries</strong> is empty, Stripe is shown to everyone not covered by Razorpay.</li>
                    <li>If a gateway is <strong>Disabled</strong>, it is never shown regardless of country.</li>
                    <li>Country codes follow ISO 3166-1 alpha-2 (e.g. <code>IN</code>, <code>US</code>, <code>GB</code>).</li>
                </Box>
            </Box>

            {/* Stripe Card */}
            <GatewayCard
                title="Stripe"
                accentColor="#635bff"
                icon={<CreditCardIcon sx={{ color: '#635bff', fontSize: 22 }} />}
                fields={STRIPE_FIELDS}
                values={stripeValues}
                onValueChange={handleStripeValueChange}
                enabled={stripeEnabled}
                onEnabledChange={setStripeEnabled}
                countriesSelected={stripeCountriesSelected}
                onCountriesChange={handleStripeCountriesChange}
                showSecrets={stripeShowSecrets}
                onToggleSecrets={() => setStripeShowSecrets(s => !s)}
                saving={stripeSaving}
                testing={stripeTesting}
                error={stripeError}
                success={stripeSuccess}
                connectionStatus={stripeStatus}
                onSave={handleStripeSave}
                onTest={handleStripeTest}
            />

            {/* Razorpay Card */}
            <GatewayCard
                title="Razorpay"
                accentColor="#3395ff"
                icon={<CurrencyRupeeIcon sx={{ color: '#3395ff', fontSize: 22 }} />}
                fields={RAZORPAY_FIELDS}
                values={rzpValues}
                onValueChange={handleRzpValueChange}
                enabled={rzpEnabled}
                onEnabledChange={setRzpEnabled}
                countriesSelected={rzpCountriesSelected}
                onCountriesChange={handleRzpCountriesChange}
                showSecrets={rzpShowSecrets}
                onToggleSecrets={() => setRzpShowSecrets(s => !s)}
                saving={rzpSaving}
                testing={rzpTesting}
                error={rzpError}
                success={rzpSuccess}
                connectionStatus={rzpStatus}
                onSave={handleRzpSave}
                onTest={handleRzpTest}
            />

        </Box>
    );
}
